import { useEffect, useRef, type HTMLAttributes } from 'react';
import { Tab } from '../../basic/Tab';
import styles from './TabBar.module.css';

export interface TabItem {
  label: string;
  value: string;
  href?: string;
  onClick?: () => void;
}

export interface TabBarProps extends Omit<HTMLAttributes<HTMLElement>, 'onChange'> {
  tabs: TabItem[];
  activeTab: string;
  onTabChange?: (value: string) => void;
  className?: string;
}

export function TabBar({
  tabs,
  activeTab,
  onTabChange,
  className,
  ...props
}: TabBarProps) {
  const tabListRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLElement | null>(null);

  const handleTabClick = (tab: TabItem) => {
    if (tab.onClick) {
      tab.onClick();
    }
    if (onTabChange) {
      onTabChange(tab.value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, currentIndex: number) => {
    let newIndex = currentIndex;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        break;
      case 'ArrowRight':
        e.preventDefault();
        newIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    if (onTabChange && newIndex !== currentIndex && tabs[newIndex]) {
      onTabChange(tabs[newIndex]!.value);
    }

    // Focus the new tab
    const tabElements = tabListRef.current?.querySelectorAll('[role="tab"]');
    if (tabElements && tabElements[newIndex]) {
      (tabElements[newIndex] as HTMLElement).focus();
    }
  };

  // Scroll active tab into view on mobile
  useEffect(() => {
    if (activeTabRef.current && tabListRef.current) {
      const container = tabListRef.current;
      const tab = activeTabRef.current;
      
      const containerRect = container.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      
      const scrollLeft = container.scrollLeft;
      const tabLeft = tabRect.left - containerRect.left + scrollLeft;
      const tabRight = tabRect.right - containerRect.left + scrollLeft;
      const containerWidth = container.clientWidth;
      
      if (tabLeft < scrollLeft) {
        container.scrollTo({ left: tabLeft - 16, behavior: 'smooth' });
      } else if (tabRight > scrollLeft + containerWidth) {
        container.scrollTo({ left: tabRight - containerWidth + 16, behavior: 'smooth' });
      }
    }
  }, [activeTab]);

  const classes = [
    styles.tabBar,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      role="tablist"
      aria-label="Navegación por pestañas"
      {...props}
    >
      <div ref={tabListRef} className={styles.tabList}>
        {tabs.map((tab, index) => {
          const isActive = activeTab === tab.value;
          
          return (
            <Tab
              key={tab.value}
              ref={isActive ? (activeTabRef as any) : undefined}
              label={tab.label}
              isActive={isActive}
              onClick={() => handleTabClick(tab)}
              href={tab.href}
              onKeyDown={(e) => handleKeyDown(e, index)}
            />
          );
        })}
      </div>
    </div>
  );
}

TabBar.displayName = 'TabBar';
